import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

export default function AdminClasses() {
  const [classes, setClasses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    maxattendees: '',
    color: '#a855f7',
    schedule: '',
  });

  const fetchClasses = async () => {
    const { data, error } = await supabase.from('classes').select('*').order('id');
    if (error) console.error('Error fetching classes:', error.message);
    else setClasses(data);
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  const openAddModal = () => {
    setEditingId(null);
    setFormData({ title: '', description: '', image: '', maxattendees: '', color: '#a855f7', schedule: '' });
    setShowModal(true);
  };

  const openEditModal = (cls) => {
    setEditingId(cls.id);
    setFormData({
      title: cls.title,
      description: cls.description,
      image: cls.image,
      maxattendees: cls.maxattendees,
      color: cls.color || '#a855f7',
      schedule: cls.schedule || '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { title, description, image, maxattendees, color, schedule } = formData;

    const { error } = editingId
      ? await supabase.from('classes').update({ title, description, image, maxattendees, color, schedule }).eq('id', editingId)
      : await supabase.from('classes').insert([{ title, description, image, maxattendees, color, schedule }]);

    if (error) alert(`Error saving: ${error.message}`);
    else {
      await fetchClasses();
      setShowModal(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta clase definitivamente?')) return;
    const { error } = await supabase.from('classes').delete().eq('id', id);
    if (error) alert('Error deleting: ' + error.message);
    else fetchClasses();
  };

  // Subir imagen y guardar la URL en classes.image
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!editingId) {
      alert('Primero guarda la clase y luego sube la imagen.');
      return;
    }

    try {
      const filePath = `${editingId}`;

      const { error: uploadError } = await supabase.storage.from('class-images').upload(filePath, file, {
        upsert: true, // <--- IMPORTANTE: permite reemplazar si ya existe
        cacheControl: '3600',
        contentType: file.type,
      });

      if (uploadError) {
        alert('No se pudo subir la imagen');
        return;
      }

      // 3) Obtener URL pública
      const { data: publicData } = supabase.storage.from('class-images').getPublicUrl(filePath);

      const publicUrl = publicData.publicUrl;

      // 4) Guardar la URL en la tabla classes.image
      const { error: updateError } = await supabase.from('classes').update({ image: publicUrl }).eq('id', editingId);

      if (updateError) {
        console.error('Error guardando image en classes:', updateError.message);
        alert('La imagen se subió, pero no se pudo guardar en la clase.');
        return;
      }

      // 5) Actualizar UI local
      setFormData((prev) => ({
        ...prev,
        image: publicUrl,
      }));

      // refrescar lista si quieres que la tabla se actualice al toque
      await fetchClasses();

      alert('Imagen actualizada correctamente 👍');
    } catch (err) {
      console.error('Error inesperado en handleFileChange:', err);
      alert('Ocurrió un error al subir la imagen.');
    }
  };

  return (
    <section className="w-[95%] md:w-[90%] lg:w-[80%] mx-auto py-6">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center justify-end mb-4">
        <button onClick={openAddModal} className="bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition w-full sm:w-auto">
          Nueva Clase
        </button>
      </div>

      {/* Desktop Table (>= md) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full table-fixed border border-sand border-collapse text-sm">
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '19%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr className="bg-sand text-left text-terracotta-dark">
              <th className="border px-3 py-2">Título</th>
              <th className="border px-3 py-2">Descripción</th>
              <th className="border px-3 py-2 text-center">Máx. Asistentes</th>
              <th className="border px-3 py-2 text-center">Color</th>
              <th className="border px-3 py-2">Imagen</th>
              <th className="border px-3 py-2 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {classes.map((cls) => (
              <tr key={cls.id} className="hover:bg-sand/40">
                <td className="border px-3 py-2 align-top break-words">{cls.title}</td>
                <td className="border px-3 py-2 align-top break-words">{cls.description}</td>
                <td className="border px-3 py-2 text-center align-top">{cls.maxattendees}</td>
                <td className="border px-3 py-2 text-center align-top">
                  <span className="inline-block w-6 h-6 rounded-full border border-gray-300" style={{ backgroundColor: cls.color || '#a855f7' }} />
                </td>
                <td className="border px-3 py-2 align-top truncate">{cls.image}</td>
                <td className="border px-3 py-2 text-center align-top">
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => openEditModal(cls)} className="text-blue-600 hover:underline">
                      Editar
                    </button>
                    <button onClick={() => handleDelete(cls.id)} className="text-red-600 hover:underline">
                      Borrar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {classes.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  No hay registros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile / Small screens: Card list (< md) */}
      <div className="md:hidden space-y-3">
        {classes.length === 0 && <div className="text-center py-4 border border-sand rounded">No hay registros.</div>}

        {classes.map((cls) => (
          <div key={cls.id} className="border border-sand rounded-lg p-4 shadow-sm bg-card">
            <div className="flex flex-col gap-2">
              {/* Row: Title */}
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Título</span>
                <span className="w-[65%] font-semibold break-words">{cls.title}</span>
              </div>
              {/* Row: Description */}
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Descripción</span>
                <span className="w-[65%] break-words">{cls.description}</span>
              </div>
              {/* Row: Max attendees */}
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Máx.</span>
                <span className="w-[65%]">{cls.maxattendees}</span>
              </div>
              {/* Row: Color */}
              <div className="flex gap-2 items-center">
                <span className="w-[35%] text-sm font-medium text-ink/70">Color</span>
                <span className="inline-block w-5 h-5 rounded-full border border-sand" style={{ backgroundColor: cls.color || '#a855f7' }} />
              </div>
              {/* Row: Image */}
              <div className="flex gap-2">
                <span className="w-[35%] text-sm font-medium text-ink/70">Imagen</span>
                <span className="w-[65%] truncate break-words">{cls.image}</span>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <button onClick={() => openEditModal(cls)} className="px-3 py-2 rounded border w-[48%] text-blue-700 border-blue-200">
                Editar
              </button>
              <button onClick={() => handleDelete(cls.id)} className="px-3 py-2 rounded border w-[48%] text-red-700 border-red-200">
                Borrar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-card p-6 rounded-lg shadow-lg w-[96%] sm:w-[85%] md:w-[70%] lg:w-[50%] xl:w-[40%] max-w-[900px]">
            <h3 className="text-lg sm:text-xl font-serif text-terracotta mb-4">{editingId ? 'Editar clase' : 'Agregar nueva clase'}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium">Título</span>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium">Descripción</span>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded min-h-[120px]"
                  required
                />
              </label>

              <div className="flex flex-col sm:flex-row gap-4">
                <label className="block w-full sm:w-[50%]">
                  <span className="text-sm font-medium">Máximo de Asistentes</span>
                  <input
                    type="number"
                    value={formData.maxattendees}
                    onChange={(e) => setFormData({ ...formData, maxattendees: Number(e.target.value) })}
                    className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded"
                    required
                  />
                </label>

                <label className="block w-full sm:w-[50%]">
                  <span className="text-sm font-medium">Imagen (nombre o URL)</span>
                  <label className="block w-full sm:w-[50%]">
                    <span className="text-sm font-medium">Imagen</span>
                    <input type="file" accept="image/*" onChange={handleFileChange} />
                  </label>
                </label>
              </div>

              <label className="block">
                <span className="text-sm font-medium">Horarios habituales</span>
                <textarea
                  value={formData.schedule}
                  onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                  className="w-full border border-sand bg-cream text-ink px-3 py-2 rounded min-h-[80px] text-sm"
                  placeholder={'Lunes 09:00 a 10:00\nMiércoles 18:45 a 19:45'}
                />
                <p className="text-xs text-ink/50 mt-1">Un horario por línea. Se muestra en la tarjeta de la clase.</p>
              </label>

              <label className="block">
                <span className="text-sm font-medium">Color preferido en el calendario</span>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="color"
                    value={formData.color || '#a855f7'}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="h-10 w-16 cursor-pointer rounded border border-sand p-1"
                  />
                  <span className="text-sm text-ink/60">{formData.color || '#a855f7'}</span>
                </div>
              </label>

              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded border border-sand text-ink hover:bg-sand/40 w-[45%] sm:w-auto">
                  Cancelar
                </button>
                <button type="submit" className="bg-terracotta text-cream px-4 py-2 rounded hover:bg-terracotta-dark transition w-[45%] sm:w-auto">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
